import * as Sentry from '@sentry/react';
import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardAction,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import StatusBadge from '@/components/StatusBadge';
import { getClientStatusDisplay } from '@/utils/status';
import { Button } from '@/components/ui/button';
import { useFetcher } from 'react-router';
import { ButtonGroup } from '@/components/ui/button-group';
import MarkdownDisplay from '@/components/MarkdownDisplay';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Timer,
  Proportions,
  Hd,
  Play,
  Video,
  Layers,
  Download,
} from 'lucide-react';
import { getAuthHeaders } from '@/utils/auth';
import { toast } from 'sonner';

import type { DeliverableWithIngest } from '..';

export default function DeliverableCard({
  deliverable,
}: {
  deliverable: DeliverableWithIngest;
}) {
  const fetcher = useFetcher();
  const deliverableStatus = deliverable.is_client_ingesting
    ? 'processing'
    : fetcher.formData
      ? (fetcher.formData.get('status') as string)
      : deliverable.status;
  const latestVersion = deliverable.latest_client_version_display;
  const [isDownloading, setIsDownloading] = useState(false);

  function handleStatusChange(status: string) {
    fetcher.submit(
      {
        id: deliverable.id,
        status,
        intent: 'updateStatus',
      },
      {
        method: 'PATCH',
      }
    );
  }

  async function handleDownload() {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const headers = await getAuthHeaders();
      if (!deliverable.id) {
        toast.error('No deliverable ID');
        return;
      }
      const response = await fetch(
        `/api/client/deliverables/${deliverable.id}/download`,
        {
          method: 'GET',
          headers,
        }
      );
      const data = await response.json();
      if (!response.ok) {
        toast.error('Download failed');
        return;
      }
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.file_name;
      link.click();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { op: 'client-download' },
        extra: { deliverableId: deliverable.id },
      });
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }

  const { label, colour, pulse } = getClientStatusDisplay(deliverableStatus);

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex flex-row gap-3 text-base">
          <StatusBadge label={label} colour={colour} pulse={pulse} />
          <Separator orientation="vertical" className="h-6! w-0.5!" />
          <span>{deliverable.deliverable_name}</span>
        </CardTitle>
        <CardAction className="flex flex-row gap-4 items-center">
          {deliverable.frame_io_client_review_url ? (
            <Button
              className="cursor-pointer"
              size={'sm'}
              variant={'outline'}
              asChild
            >
              <a
                href={deliverable.frame_io_client_review_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Review
              </a>
            </Button>
          ) : (
            <Button
              className="cursor-pointer"
              size={'sm'}
              variant={'outline'}
              disabled={true}
            >
              Review
            </Button>
          )}
          <Separator orientation="vertical" className="h-8! w-0.5!" />
          {deliverable.status !== 'approved' ? (
            <ButtonGroup>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    className="cursor-pointer"
                    disabled={deliverableStatus !== 'client_review'}
                    size={'sm'}
                    variant={'outline'}
                  >
                    Needs Revision
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogTitle>Send back to the Editor?</DialogTitle>
                  <DialogDescription>
                    This video will be sent back for revision and the editor
                    will be notified.
                  </DialogDescription>
                  <div className="flex flex-row gap-2 justify-end">
                    <DialogClose asChild>
                      <Button variant={'outline'} className="cursor-pointer">
                        Cancel
                      </Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button
                        className="cursor-pointer"
                        onClick={() => {
                          handleStatusChange('editor_revision');
                        }}
                      >
                        Confirm
                      </Button>
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    className="cursor-pointer"
                    disabled={deliverableStatus !== 'client_review'}
                    size={'sm'}
                    variant={'outline'}
                  >
                    Approved
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogTitle>Approve video?</DialogTitle>
                  <DialogDescription>
                    This will mark the video as complete. This action cannot be
                    undone.
                  </DialogDescription>
                  <div className="flex flex-row gap-2 justify-end">
                    <DialogClose asChild>
                      <Button variant={'outline'} className="cursor-pointer">
                        Cancel
                      </Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button
                        className="cursor-pointer"
                        onClick={() => {
                          handleStatusChange('approved');
                        }}
                      >
                        Confirm
                      </Button>
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>
            </ButtonGroup>
          ) : (
            <Button
              variant={'outline'}
              className="cursor-pointer"
              size={'sm'}
              onClick={() => handleDownload()}
              disabled={isDownloading}
            >
              <Download />
              {isDownloading ? 'Preparing...' : 'Download'}
            </Button>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="flex flex-row gap-8 w-full text-xs">
          {latestVersion !== '0' ? (
            <Tooltip delayDuration={800}>
              <TooltipTrigger className="flex flex-row gap-3 items-center">
                <span className="text-right">
                  <Layers className="w-4 h-4" />
                </span>
                <span>V{latestVersion}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Latest Version</p>
              </TooltipContent>
            </Tooltip>
          ) : null}

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Video className="w-4 h-4" />
              </span>
              <span>{deliverable.video_type}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Video Type</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Timer className="w-4 h-4" />
              </span>
              <span>{deliverable.length_range}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Length Range</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Proportions className="w-4 h-4" />
              </span>
              <span>{deliverable.aspect_ratio}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Aspect Ratio</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Hd className="w-4 h-4" />
              </span>
              <span>{deliverable.resolution}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resolution</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={800}>
            <TooltipTrigger className="flex flex-row gap-3 items-center">
              <span className="text-right">
                <Play className="w-4 h-4" />
              </span>
              <span>{deliverable.frame_rate}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Frame Rate</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {deliverable.notes && (
          <div className='mt-5'>
            <span className="text-sm">Notes</span>
            <MarkdownDisplay
              content={deliverable.notes}
              size='sm'
              variant='simple'
              className="mt-2 text-muted-foreground prose-headings:text-muted-foreground prose-strong:text-muted-foreground prose-a:text-muted-foreground prose-blockquote:text-muted-foreground"
              expandable={true}
              lines={5}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
